import { queryOptions } from '@tanstack/react-query'

import { fetchJson, path } from '@/api/client'

/**
 * `/api/stages*` — the backgrounds a story can be played on, as a parent edits them: the list with
 * what each place offers (water, ground, sky…), its details (name, world, used in stories, notes)
 * and its **living scenery** — the puppets (trees, clouds, fish…) drawn between the picture and the
 * actors (docs/SKEL-STORY.md §6 Q7). A stage's scenery is `auto` (chosen per story, deterministic
 * by the story seed) until someone lays it out by hand (`custom`); DELETE puts it back to auto.
 *
 * Uids contain `/` and `:` (`bg:nature/meadow`, `live:beach/beach_day`) and go into the path
 * verbatim (`path``), like `api/annotations.ts`. `/api/stages/scenery-candidates` cannot clash
 * with a uid: every uid has a colon.
 *
 * The routes are built by the backend track in parallel, so every reader is lenient: a missing
 * optional field reads as "none" / "unknown", never as a crash.
 */

/* -------------------------------------------------------------------------- */
/* Shapes                                                                      */
/* -------------------------------------------------------------------------- */

export type StageKind = 'background' | 'live'

/** Where a piece of scenery lives: up in the sky, the middle distance, or on the ground. */
export const SCENERY_BANDS = ['sky', 'mid', 'ground'] as const
export type SceneryBand = (typeof SCENERY_BANDS)[number]

export interface StageAnnotationState {
  /** `seed | proposed | draft | reviewed | published` (open). */
  status: string
  rev: number
  published_rev: number | null
  lint_errors: number
  lint_warnings: number
}

/** A row of `GET /api/stages`. */
export interface StageRow {
  uid: string
  name: string
  kind: StageKind
  /** The world stories find it in (the details may move it). */
  world: string
  /** The world its picture came from in the catalog. */
  catalog_world: string | null
  thumb: string | null
  poster: string | null
  annotation: StageAnnotationState
  /** What the place offers: `{water: true, ground: true, sky: false, …}`. */
  features: Record<string, boolean>
  /** Anchor kinds marked on it (`door`, `log`, …). */
  anchors: string[]
  scenery: { mode: 'auto' | 'custom'; count: number }
  /** False = stories never pick it. */
  enabled: boolean
  /** What looks wrong, in plain sentences the server writes (`No ground marked to stand on`, …). */
  flags: string[]
}

export interface StageWorld {
  id: string
  count: number
}

export interface StageList {
  items: StageRow[]
  worlds: StageWorld[]
}

export interface StageFilters {
  world?: string
  q?: string
  /** Only the ones with something to look at (flags, check errors). */
  needs_look?: boolean
}

/** Longest name / notes the server keeps (`stages/store.py` `MAX_NAME`, `MAX_NOTES`; longer is cut). */
export const MAX_STAGE_NAME = 80
export const MAX_STAGE_NOTES = 2000
/** Most things a stage's living background may hold (`stages/models.py` `MAX_ITEMS`; more is a 422). */
export const MAX_SCENERY_ITEMS = 24

/** `GET/PUT /api/stages/{uid}/details`. */
export interface StageDetails {
  rev: number
  name: string
  world: string
  enabled: boolean
  notes: string
  /** The picture's own name and world in the catalog (read only; empty when the server does not say). */
  catalog_name?: string
  catalog_world?: string | null
}

export type StageDetailsPatch = Pick<StageDetails, 'name' | 'world' | 'enabled' | 'notes'>

export interface SceneryDrift {
  /** Percent of the stage width per second (negative drifts left). */
  dx_per_s: number
  /** Comes back in on the other side. */
  wrap: boolean
}

/** One piece of scenery. `x`/`y` are the FEET point in percent of the stage (0–100). */
export interface SceneryItem {
  id: string
  puppet_uid: string
  slug: string
  name: string
  band: SceneryBand
  x: number
  y: number
  /** Height in percent of the stage height. */
  height_pct: number
  flip: boolean
  /** The ambient clip it loops (`sway`, `drift`, `bob`…); null = stands still. */
  clip: string | null
  drift: SceneryDrift | null
  /** Drawing order: higher is in front. */
  z: number
  /** Client-side only: the picture to draw (never sent). */
  thumb?: string | null
}

export interface SceneryLayout {
  rev: number
  mode: 'auto' | 'custom'
  items: SceneryItem[]
}

/** A row of `GET /api/stages/scenery-candidates`: a rigged catalog thing that can be scenery. */
export interface SceneryCandidate {
  puppet_uid: string
  slug: string
  name: string
  plan: string | null
  thumb: string | null
  /** Clips it can loop. */
  clips: string[]
  /** The clip it loops by default. */
  ambient: string | null
  /** Where it belongs by default. */
  band: SceneryBand
  /** Its usual height in percent of the stage (at `default_y` on the stage asked about). */
  height_pct: number
  /** Where its feet go by default on the stage asked about (`stage_uid`); null when not given. */
  default_y: number | null
}

export interface CandidateFilters {
  world?: string
  q?: string
  /** Rank for this stage (its world first) and size `height_pct` / `default_y` for its picture. */
  stage_uid?: string
  /** The server's page (default 80, at most 500). */
  limit?: number
}

export interface StagePreviewRequest {
  /** An unsaved layout to try (the saved or automatic one when left out). */
  scenery?: SceneryItem[]
  /** A puppet uid who walks across; null = nobody. */
  walker?: string | null
}

/** A preview bundle, plus where its relative asset paths resolve from. */
export interface StagePreview {
  bundle: unknown
  bundleUrl: string
}

/* -------------------------------------------------------------------------- */
/* Readers (lenient)                                                           */
/* -------------------------------------------------------------------------- */

const num = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : d)
const str = (v: unknown, d = ''): string => (typeof v === 'string' ? v : d)
const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v ? v : null)
const strList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [])

export const isBand = (v: unknown): v is SceneryBand => typeof v === 'string' && (SCENERY_BANDS as readonly string[]).includes(v)

function itemsOf(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object') {
    const r = raw as { items?: unknown }
    if (Array.isArray(r.items)) return r.items
  }
  return []
}

export function readStageRow(raw: unknown): StageRow | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (typeof r.uid !== 'string' || !r.uid) return null
  const a = (r.annotation && typeof r.annotation === 'object' ? r.annotation : {}) as Record<string, unknown>
  const s = (r.scenery && typeof r.scenery === 'object' ? r.scenery : {}) as Record<string, unknown>
  const features: Record<string, boolean> = {}
  if (r.features && typeof r.features === 'object') for (const [k, v] of Object.entries(r.features as Record<string, unknown>)) features[k] = v === true
  return {
    uid: r.uid,
    name: str(r.name) || r.uid,
    kind: r.kind === 'live' || r.kind === 'live_background' || r.uid.startsWith('live:') ? 'live' : 'background',
    world: str(r.world),
    catalog_world: strOrNull(r.catalog_world),
    thumb: strOrNull(r.thumb),
    poster: strOrNull(r.poster),
    annotation: {
      status: str(a.status, 'seed'),
      rev: num(a.rev, 0),
      published_rev: typeof a.published_rev === 'number' ? a.published_rev : null,
      lint_errors: num(a.lint_errors, 0),
      lint_warnings: num(a.lint_warnings, 0),
    },
    features,
    anchors: strList(r.anchors),
    scenery: { mode: s.mode === 'custom' ? 'custom' : 'auto', count: num(s.count, 0) },
    enabled: r.enabled !== false,
    flags: strList(r.flags),
  }
}

export function readSceneryItem(raw: unknown, index = 0): SceneryItem | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const puppet = str(r.puppet_uid)
  if (!puppet) return null
  const d = r.drift && typeof r.drift === 'object' ? (r.drift as Record<string, unknown>) : null
  return {
    id: str(r.id) || `s_${index + 1}`,
    puppet_uid: puppet,
    slug: str(r.slug),
    name: str(r.name) || str(r.slug) || puppet,
    band: isBand(r.band) ? r.band : 'ground',
    x: num(r.x, 50),
    y: num(r.y, 85),
    height_pct: num(r.height_pct, 20),
    flip: r.flip === true,
    clip: strOrNull(r.clip),
    drift: d ? { dx_per_s: num(d.dx_per_s, 1), wrap: d.wrap !== false } : null,
    z: num(r.z, index),
    thumb: strOrNull(r.thumb),
  }
}

export function readSceneryItems(raw: unknown): SceneryItem[] {
  return itemsOf(raw)
    .map((x, i) => readSceneryItem(x, i))
    .filter((x): x is SceneryItem => x !== null)
}

export function readSceneryLayout(raw: unknown): SceneryLayout {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  return { rev: num(r.rev, 0), mode: r.mode === 'custom' ? 'custom' : 'auto', items: readSceneryItems(r.items) }
}

export function readCandidate(raw: unknown): SceneryCandidate | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const puppet = str(r.puppet_uid)
  if (!puppet) return null
  return {
    puppet_uid: puppet,
    slug: str(r.slug),
    name: str(r.name) || str(r.slug) || puppet,
    plan: strOrNull(r.plan),
    thumb: strOrNull(r.thumb),
    clips: strList(r.clips),
    ambient: strOrNull(r.ambient),
    band: isBand(r.band) ? r.band : 'ground',
    height_pct: num(r.height_pct, 20),
    default_y: typeof r.default_y === 'number' && Number.isFinite(r.default_y) ? r.default_y : null,
  }
}

export function readStageDetails(raw: unknown): StageDetails {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  return {
    rev: num(r.rev, 0),
    name: str(r.name),
    world: str(r.world),
    enabled: r.enabled !== false,
    notes: str(r.notes),
    catalog_name: str(r.catalog_name),
    catalog_world: strOrNull(r.catalog_world),
  }
}

/** Where a preview's relative asset paths resolve when the server does not say. */
export function defaultStagePreviewUrl(uid: string): string {
  return path`/api/stages/${uid}/preview/bundle.json`
}

/** The bundle itself, or `{bundle, bundle_url?}`. */
export function readStagePreview(uid: string, raw: unknown): StagePreview {
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>
    if (r.bundle && typeof r.bundle === 'object') {
      const url = typeof r.bundle_url === 'string' ? r.bundle_url : typeof r.base_url === 'string' ? r.base_url : defaultStagePreviewUrl(uid)
      return { bundle: r.bundle, bundleUrl: url }
    }
  }
  return { bundle: raw, bundleUrl: defaultStagePreviewUrl(uid) }
}

/** Exactly the contract's fields, rounded — what a PUT or a preview sends. */
export function toWireItem(i: SceneryItem): Omit<SceneryItem, 'thumb'> {
  const r2 = (v: number) => Math.round(v * 100) / 100
  return {
    id: i.id,
    puppet_uid: i.puppet_uid,
    slug: i.slug,
    name: i.name,
    band: i.band,
    x: r2(i.x),
    y: r2(i.y),
    height_pct: r2(i.height_pct),
    flip: i.flip,
    clip: i.clip,
    drift: i.drift ? { dx_per_s: r2(i.drift.dx_per_s), wrap: i.drift.wrap } : null,
    z: i.z,
  }
}

/* -------------------------------------------------------------------------- */
/* Fetchers                                                                    */
/* -------------------------------------------------------------------------- */

/** `GET /api/stages?world=&q=&needs_look=`. */
export async function listStages(filters: StageFilters = {}, signal?: AbortSignal): Promise<StageList> {
  const raw = await fetchJson<unknown>('/api/stages', {
    query: { world: filters.world || undefined, q: filters.q?.trim() || undefined, needs_look: filters.needs_look ? 1 : undefined },
    signal,
  })
  const items = itemsOf(raw)
    .map(readStageRow)
    .filter((x): x is StageRow => x !== null)
  const worldsRaw = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as { worlds?: unknown }).worlds : undefined
  const worlds = (Array.isArray(worldsRaw) ? worldsRaw : [])
    .map((w) => (typeof w === 'string' ? { id: w, count: 0 } : w && typeof w === 'object' ? { id: str((w as { id?: unknown }).id), count: num((w as { count?: unknown }).count, 0) } : null))
    .filter((w): w is StageWorld => w !== null && w.id !== '')
  return { items, worlds }
}

/** `GET /api/stages/{uid}` → `{item, details, scenery}`: one stage's list row with its saved sections. */
export async function getStage(uid: string, signal?: AbortSignal): Promise<{ item: StageRow | null; details: StageDetails | null; scenery: SceneryLayout | null }> {
  const raw = (await fetchJson<unknown>(path`/api/stages/${uid}`, { signal })) as Record<string, unknown> | null
  return {
    item: readStageRow(raw?.item),
    details: raw?.details && typeof raw.details === 'object' ? readStageDetails(raw.details) : null,
    scenery: raw?.scenery && typeof raw.scenery === 'object' ? readSceneryLayout(raw.scenery) : null,
  }
}

export async function getStageDetails(uid: string, signal?: AbortSignal): Promise<StageDetails> {
  return readStageDetails(await fetchJson<unknown>(path`/api/stages/${uid}/details`, { signal }))
}

/** `PUT …/details` `{base_rev, name, world, enabled, notes}`; `409` on a stale rev. */
export async function putStageDetails(uid: string, baseRev: number, patch: StageDetailsPatch): Promise<StageDetails> {
  return readStageDetails(await fetchJson<unknown>(path`/api/stages/${uid}/details`, { method: 'PUT', body: { base_rev: baseRev, ...patch } }))
}

export async function getScenery(uid: string, signal?: AbortSignal): Promise<SceneryLayout> {
  return readSceneryLayout(await fetchJson<unknown>(path`/api/stages/${uid}/scenery`, { signal }))
}

/** `PUT …/scenery` `{base_rev, mode: 'custom', items}` — every story on the stage uses it; `409` on a stale rev. */
export async function putScenery(uid: string, baseRev: number, items: readonly SceneryItem[]): Promise<SceneryLayout> {
  return readSceneryLayout(await fetchJson<unknown>(path`/api/stages/${uid}/scenery`, { method: 'PUT', body: { base_rev: baseRev, mode: 'custom', items: items.map(toWireItem) } }))
}

/** `DELETE …/scenery?base_rev=` — back to automatic. Answers the new layout, or nothing (then it is read again). */
export async function resetScenery(uid: string, baseRev?: number): Promise<SceneryLayout | null> {
  const raw = await fetchJson<unknown>(path`/api/stages/${uid}/scenery`, { method: 'DELETE', query: { base_rev: baseRev } })
  return raw && typeof raw === 'object' ? readSceneryLayout(raw) : null
}

/** `GET …/scenery/suggest?seed=` — what the studio would choose (the same seed, the same answer). */
export async function suggestScenery(uid: string, seed?: number, signal?: AbortSignal): Promise<SceneryItem[]> {
  return readSceneryItems(await fetchJson<unknown>(path`/api/stages/${uid}/scenery/suggest`, { query: { seed }, signal }))
}

/** `GET /api/stages/scenery-candidates?world=&q=&stage_uid=&limit=` → `{items, total}`. */
export async function listSceneryCandidates(filters: CandidateFilters = {}, signal?: AbortSignal): Promise<SceneryCandidate[]> {
  const raw = await fetchJson<unknown>('/api/stages/scenery-candidates', {
    query: { world: filters.world || undefined, q: filters.q?.trim() || undefined, stage_uid: filters.stage_uid || undefined, limit: filters.limit },
    signal,
  })
  return itemsOf(raw)
    .map(readCandidate)
    .filter((x): x is SceneryCandidate => x !== null)
}

/** `POST …/preview {scenery?, walker?}` → a bundle that plays the stage (and a walker, when asked). */
export async function previewStage(uid: string, req: StagePreviewRequest = {}, signal?: AbortSignal): Promise<StagePreview> {
  const body: Record<string, unknown> = {}
  if (req.scenery) body.scenery = req.scenery.map(toWireItem)
  if (req.walker !== undefined) body.walker = req.walker
  return readStagePreview(uid, await fetchJson<unknown>(path`/api/stages/${uid}/preview`, { method: 'POST', body, signal }))
}

/* -------------------------------------------------------------------------- */
/* Keys + query options                                                        */
/* -------------------------------------------------------------------------- */

function clean(f: object): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const [k, v] of Object.entries(f)) {
    if (typeof v === 'string' && v.trim() !== '') out[k] = v.trim()
    else if (typeof v === 'number' && Number.isFinite(v)) out[k] = v
    else if (v === true) out[k] = true
  }
  return out
}

/** Own key family (`['stages', …]`) so a save anywhere can refresh every stage view by prefix. */
export const stageKeys = {
  all: () => ['stages'] as const,
  list: (f: StageFilters = {}) => ['stages', 'list', clean(f)] as const,
  one: (uid: string) => ['stages', 'one', uid] as const,
  details: (uid: string) => ['stages', 'details', uid] as const,
  scenery: (uid: string) => ['stages', 'scenery', uid] as const,
  candidates: (f: CandidateFilters = {}) => ['stages', 'candidates', clean(f)] as const,
}

export const stagesQuery = (f: StageFilters = {}) =>
  queryOptions({
    queryKey: stageKeys.list(f),
    queryFn: ({ signal }) => listStages(f, signal),
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  })

export const stageDetailsQuery = (uid: string) =>
  queryOptions({
    queryKey: stageKeys.details(uid),
    queryFn: ({ signal }) => getStageDetails(uid, signal),
    staleTime: 5_000,
    enabled: uid.length > 0,
  })

export const sceneryQuery = (uid: string) =>
  queryOptions({
    queryKey: stageKeys.scenery(uid),
    queryFn: ({ signal }) => getScenery(uid, signal),
    staleTime: 5_000,
    enabled: uid.length > 0,
  })

/** One stage's row (header, picture, catalog world). */
export const stageQuery = (uid: string) =>
  queryOptions({
    queryKey: stageKeys.one(uid),
    queryFn: ({ signal }) => getStage(uid, signal),
    staleTime: 5_000,
    enabled: uid.length > 0,
  })

export const sceneryCandidatesQuery = (f: CandidateFilters = {}) =>
  queryOptions({
    queryKey: stageKeys.candidates(f),
    queryFn: ({ signal }) => listSceneryCandidates(f, signal),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  })
